<template>
  <div>
    <!-- PIN Dialog (e-imza) -->
    <v-dialog v-model="eimza.pinDialog" max-width="460">
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="primary">mdi-shield-key</v-icon>
          e-İmza Doğrulaması
        </v-card-title>
        <v-card-text>
          <v-alert v-if="eimzaCert" type="info" dense class="mb-4">
            <div><strong>{{ eimzaCert.subjectName }}</strong></div>
            <div v-if="eimzaCert.subjectSerial">TC: {{ eimzaCert.subjectSerial }}</div>
            <div v-if="eimzaCert.isExpired" class="red--text mt-1">
              <v-icon small color="red">mdi-alert</v-icon> Sertifikanın süresi dolmuş!
            </div>
          </v-alert>
          <p class="body-2 mb-3">SMS sayfasına erişmek için e-İmza PIN kodunuzu giriniz.</p>
          <v-text-field
            v-model="eimza.pin"
            label="PIN Kodu"
            type="password"
            outlined
            dense
            :disabled="eimza.loginLoading"
            :error-messages="eimza.loginError"
            @keyup.enter="doSignLogin"
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :loading="eimza.loginLoading"
            :disabled="!eimza.pin || eimza.loginLoading"
            @click="doSignLogin"
          >
            <v-icon left>mdi-check</v-icon>
            Doğrula
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Kart -->
    <v-card :style="cardStyle">
      <card-toolbar :title="title"></card-toolbar>

      <!-- BTK uyarısı her zaman gösterilir -->
      <v-alert type="info" class="ma-3" border="left" colored-border elevation="2">
        <div class="subtitle-2 mb-1">Toplu SMS Gönderimlerinde e-İmza / Mobil İmza Zorunluluğu</div>
        <div class="body-2">
          BTK tarafından alınan kurul kararı kapsamında, <strong>01.04.2026</strong> itibarıyla
          SMS gönderim arayüzlerine erişim yalnızca aboneliğin yetkilisi veya SMS göndermeye
          yetkili kişi tarafından <strong>e-İmza</strong> veya <strong>Mobil İmza</strong> ile yapılabilecektir.
        </div>
      </v-alert>

      <!-- Deadline öncesi: içerik her zaman görünür -->
      <template v-if="beforeDeadline">
        <slot></slot>
      </template>

      <!-- Deadline sonrası: imza zorunlu -->
      <template v-else>
        <!-- Mobil imza ile bağlıysa direkt içeriği göster -->
        <template v-if="mobileSignatureApproved">
          <v-alert type="success" class="ma-3" dense border="left">
            <v-icon left>mdi-cellphone-check</v-icon>
            <strong>{{ authPhone }}</strong> numarası ile Mobil İmza doğrulaması tamamlandı. SMS gönderimlerinize devam edebilirsiniz.
            <v-btn text small color="white" class="ml-2" @click="doMobileLogout" :loading="mobile.logoutLoading">
              <v-icon left small>mdi-logout</v-icon>Bağlantıyı Kes
            </v-btn>
          </v-alert>
          <slot></slot>
        </template>

        <!-- e-imza ile bağlıysa direkt içeriği göster -->
        <template v-else-if="eimza.authenticated">
          <v-alert v-if="eimzaCert" type="success" class="ma-3" dense border="left">
            <v-icon left>mdi-shield-check</v-icon>
            <strong>{{ eimzaCert.subjectName }}</strong>
            <span v-if="eimzaCert.subjectSerial">&nbsp;— TC: {{ eimzaCert.subjectSerial }}</span>
            &nbsp;olarak e-İmza ile doğrulandınız.
          </v-alert>
          <slot></slot>
        </template>

        <!-- Henüz bağlı değil: tab seçimi -->
        <template v-else>
          <v-tabs v-model="activeTab" class="mx-3 mt-2" color="primary">
            <v-tab>
              <v-icon left small>mdi-shield-key</v-icon>
              e-İmza ile Bağlan
            </v-tab>
            <v-tab>
              <v-icon left small>mdi-cellphone-key</v-icon>
              Mobil İmza ile Bağlan
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <!-- TAB 1: e-İmza -->
            <v-tab-item>
              <div v-if="!eimza.checked" style="text-align:center; padding:60px 20px;">
                <v-progress-circular indeterminate color="primary" size="56"></v-progress-circular>
                <p class="body-1 mt-4 grey--text">e-İmza cihazı kontrol ediliyor...</p>
              </div>

              <div v-else-if="eimza.devices.length === 0" style="text-align:center; padding:40px 24px 48px;">
                <v-icon size="72" color="red lighten-2">mdi-usb-flash-drive-outline</v-icon>
                <h2 class="mt-4 mb-2 red--text text--darken-1">e-İmza Cihazı Bulunamadı</h2>
                <p class="body-1 grey--text mb-6">
                  SMS gönderme sayfasına erişmek için e-İmza cihazınızı takmanız gerekmektedir.
                </p>
                <v-card outlined class="mx-auto mb-6 text-left" max-width="480" style="border-radius:10px;">
                  <v-card-title class="subtitle-1 font-weight-bold pb-1">
                    <v-icon left color="primary" small>mdi-information-outline</v-icon>
                    Nasıl Kullanılır?
                  </v-card-title>
                  <v-divider></v-divider>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-avatar size="26" color="primary" class="white--text" style="font-size:13px;font-weight:700;">1</v-avatar>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="body-2" style="white-space:normal;">
                          Aşağıdan işletim sisteminize uygun <strong>Sümen e-İmza Uygulaması</strong>'nı indirin ve bilgisayarınıza kurun.
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider inset></v-divider>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-avatar size="26" color="primary" class="white--text" style="font-size:13px;font-weight:700;">2</v-avatar>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="body-2" style="white-space:normal;">
                          Kurulum tamamlandıktan sonra <strong>Sümen uygulamasını başlatın</strong> ve arka planda çalışır durumda bırakın.
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider inset></v-divider>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-avatar size="26" color="primary" class="white--text" style="font-size:13px;font-weight:700;">3</v-avatar>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="body-2" style="white-space:normal;">
                          <strong>e-İmza cihazınızı</strong> bilgisayarınızın USB portuna takın.
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider inset></v-divider>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-avatar size="26" color="primary" class="white--text" style="font-size:13px;font-weight:700;">4</v-avatar>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="body-2" style="white-space:normal;">
                          Bu sayfada beliren <strong>PIN kodu alanına</strong> e-İmzanızın PIN'ini girin ve doğrulayın.
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider inset></v-divider>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-avatar size="26" color="success" class="white--text" style="font-size:13px;font-weight:700;">
                          <v-icon small color="white">mdi-check</v-icon>
                        </v-avatar>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="body-2" style="white-space:normal;">
                          PIN doğrulandıktan sonra <strong>SMS gönderimlerinize</strong> hemen başlayabilirsiniz.
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
                <div class="d-flex justify-center flex-wrap" style="gap:16px;">
                  <v-btn color="blue darken-3" dark large href="/SumenAPP-Setup-1.2.10.exe" download style="min-width:200px; text-transform:none; border-radius:8px;">
                    <v-icon left>mdi-microsoft-windows</v-icon>
                    Windows Sürümü
                  </v-btn>
                  <v-btn color="grey darken-3" dark large href="/SumenApp_1_2_8.pkg" download style="min-width:200px; text-transform:none; border-radius:8px;">
                    <v-icon left>mdi-apple</v-icon>
                    macOS Sürümü
                  </v-btn>
                </div>
                <v-btn color="primary" outlined class="mt-6" @click="connectEimza">
                  <v-icon left>mdi-refresh</v-icon>
                  Tekrar Dene
                </v-btn>
              </div>

              <div v-else style="text-align:center; padding:60px 20px;">
                <v-icon size="72" color="primary">mdi-shield-key-outline</v-icon>
                <h2 class="mt-4 mb-2">e-İmza Doğrulaması Gerekiyor</h2>
                <p class="body-1 grey--text mb-4">
                  SMS sayfasına erişmek için e-İmza PIN kodunuzu girmeniz gerekmektedir.
                </p>
                <v-btn color="primary" large @click="eimza.pinDialog = true">
                  <v-icon left>mdi-shield-key</v-icon>
                  PIN Gir
                </v-btn>
              </div>
            </v-tab-item>

            <!-- TAB 2: Mobil İmza -->
            <v-tab-item>
              <div style="text-align:center; padding:40px 24px 48px;">
                <v-icon size="72" color="deep-purple">mdi-cellphone-key</v-icon>
                <h2 class="mt-4 mb-2">Mobil İmza ile Doğrulama</h2>

                <!-- Telefon numarası yoksa uyarı -->
                <template v-if="!authPhone">
                  <v-alert type="warning" class="mx-auto mt-4" max-width="480" border="left" colored-border elevation="2">
                    <div class="subtitle-2 mb-1">Telefon Numarası Gerekli</div>
                    <div class="body-2">
                      Mobil İmza ile doğrulama yapabilmek için hesabınıza bir telefon numarası atanmış olması gerekmektedir.
                      Lütfen hesap ayarlarınızdan telefon numaranızı ekleyiniz.
                    </div>
                  </v-alert>
                  <v-btn color="primary" outlined class="mt-4" :to="{ name: 'settings.index' }">
                    <v-icon left>mdi-cog</v-icon>
                    Hesap Ayarlarına Git
                  </v-btn>
                </template>

                <!-- Telefon numarası varsa -->
                <template v-else>
                  <v-alert type="info" class="mx-auto mt-4" max-width="480" border="left" colored-border elevation="2">
                    <div class="body-2">
                      Mobil İmza doğrulaması <strong>{{ authPhone }}</strong> numaralı hat üzerinden gerçekleştirilecektir.
                      Operatörünüzün mobil imza hizmetinin aktif olduğundan emin olunuz.
                    </div>
                  </v-alert>
                  <v-alert type="info" class="mx-auto mt-3" max-width="480" border="left" colored-border elevation="2">
                    <div class="subtitle-2 mb-1">
                      <v-icon left small>mdi-information-outline</v-icon>
                      Nasıl çalışır?
                    </div>
                    <div class="body-2">
                      Aşağıdaki butona tıkladığınızda <strong>{{ authPhone }}</strong> numaralı telefonunuza
                      mobil imza doğrulama bildirimi gönderilecektir.
                      Telefonunuzda beliren bildirimi <strong>onaylayarak</strong> doğrulamayı tamamlayabilirsiniz.
                    </div>
                    <div class="body-2 mt-2">
                      <v-icon small color="warning">mdi-clock-outline</v-icon>
                      İstek gönderildikten sonra birkaç saniye içinde telefonunuza bildirim ulaşacaktır.
                    </div>
                  </v-alert>
                  <v-btn
                    color="deep-purple"
                    dark
                    large
                    class="mt-6"
                    :loading="mobile.loginLoading"
                    @click="doMobileLogin"
                  >
                    <v-icon left>mdi-cellphone-check</v-icon>
                    Mobil İmza Doğrulamasını Başlat
                  </v-btn>
                  <div v-if="mobile.loginLoading" class="mt-4">
                    <v-alert type="info" dense max-width="480" class="mx-auto" border="left">
                      <v-icon left small>mdi-cellphone-wireless</v-icon>
                      Telefonunuza bildirim gönderildi. Lütfen bekleyin ve bildirimi onaylayın...
                    </v-alert>
                  </div>
                  <div v-if="mobile.error" class="mt-4">
                    <v-alert type="error" dense max-width="480" class="mx-auto">{{ mobile.error }}</v-alert>
                  </div>
                </template>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </template>
      </template>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import { isEimzaRequired } from '../../misc/eimza';
import CardToolbar from './CardToolbar.vue';

export default {
  name: 'EimzaGate',
  components: { CardToolbar },
  props: {
    title: {
      type: String,
      required: true,
    },
    autoOpenPin: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    activeTab: 0,
    eimza: {
      ws: null,
      checked: false,
      devices: [],
      authenticated: false,
      pinDialog: false,
      pin: '',
      loginLoading: false,
      loginError: '',
      pendingResolvers: {},
      pollTimer: null,
      logoutSent: false,
    },
    mobile: {
      loginLoading: false,
      logoutLoading: false,
      error: '',
    },
  }),
  computed: {
    beforeDeadline() {
      return !isEimzaRequired(this.$store.state.user.id);
    },
    eimzaCert() {
      for (var i = 0; i < this.eimza.devices.length; i++) {
        var device = this.eimza.devices[i];
        if (device.certs && device.certs.length > 0) {
          return device.certs[0];
        }
      }
      return null;
    },
    cardStyle() {
      return this.beforeDeadline ? '' : 'position:relative; overflow:hidden;';
    },
    // store'daki mobile_signature verisine bak
    mobileSignatureApproved() {
      var user = this.$store.state.user;
      if (!user) return false;
      var approved = user.is_mobile_signature_approved;
      var expiredAt = user.mobile_signature_expired_at;
      if (!approved) return false;
      if (!expiredAt) return false;
      return new Date(expiredAt) > new Date();
    },
    // auth_phone: me endpoint'inden gelen telefon numarası
    authPhone() {
      return this.$store.state.user ? this.$store.state.user.auth_phone || null : null;
    },
    // e-imza store'dan: esignature_is_valid=1 ve expires_at süresi geçmemiş
    eimzaStoreApproved() {
      var user = this.$store.state.user;
      if (!user) return false;
      if (!user.esignature_is_valid) return false;
      if (!user.esignature_expires_at) return false;
      return new Date(user.esignature_expires_at) > new Date();
    },
  },
  created() {
    if (this.beforeDeadline) return;
    // Her sayfa açılışında /me'den güncel durumu çek, sonra gerekirse e-imza bağlantısını başlat
    this.fetchMeAndInit();
  },
  beforeDestroy() {
    this.stopPolling();
    if (this.eimza.ws) {
      this.eimza.ws.close();
    }
  },
  methods: {
    ...mapActions({ send: 'sendBackend' }),
    ...mapMutations(['updateMobileSignature', 'updateEsignature']),

    // ---- Mobil İmza ----
    fetchMeAndInit() {
      // Her sayfa açılışında /me'den güncel durumu çek
      this.send({ route: 'me' })
        .then((res) => {
          var data = res.data.data;
          var mobileSignature = data.mobile_signature || {};
          var isMobileApproved = !!mobileSignature.is_mobile_signature_approved;
          var mobileExpiredAt = mobileSignature.mobile_signature_expired_at || null;
          var mobileValid = isMobileApproved && mobileExpiredAt && new Date(mobileExpiredAt) > new Date();

          var user = data.user || {};
          var isEimzaValid = !!user.esignature_is_valid;
          var eimzaExpiresAt = user.esignature_expires_at || null;
          var eimzaValid = isEimzaValid && eimzaExpiresAt && new Date(eimzaExpiresAt) > new Date();

          // Store'u güncelle
          this.updateMobileSignature({
            is_mobile_signature_approved: isMobileApproved,
            mobile_signature_expired_at: mobileExpiredAt,
            auth_phone: data.auth_phone || null,
          });
          this.updateEsignature({
            esignature_is_valid: user.esignature_is_valid,
            esignature_expires_at: eimzaExpiresAt,
          });

          // Mobil imza geçerliyse e-imza WebSocket'e gerek yok
          if (mobileValid) return;

          // e-imza geçerliyse WebSocket bağlan ama authenticated olarak başlat
          if (eimzaValid) {
            this.eimza.authenticated = true;
          }
          this.connectEimza();
        })
        .catch(() => {
          // /me başarısız olursa yine de e-imza bağlantısını dene
          this.connectEimza();
        });
    },

    doMobileLogin() {
      this.mobile.loginLoading = true;
      this.mobile.error = '';
      this.send({ route: 'mobile-signature/login' })
        .then(() => {
          // Başarılı: /me'den güncel veriyi çek ve store'u güncelle
          return this.send({ route: 'me' }).then((res) => {
            var data = res.data.data;
            var mobileSignature = data.mobile_signature || {};
            this.updateMobileSignature({
              is_mobile_signature_approved: !!mobileSignature.is_mobile_signature_approved,
              mobile_signature_expired_at: mobileSignature.mobile_signature_expired_at || null,
              auth_phone: data.auth_phone || null,
            });
          });
        })
        .catch((err) => {
          this.mobile.error = typeof err === 'string' ? err : 'Mobil imza doğrulaması başarısız oldu.';
        })
        .finally(() => {
          this.mobile.loginLoading = false;
        });
    },

    doMobileLogout() {
      this.mobile.logoutLoading = true;
      this.send({ route: 'mobile-signature/logout' })
        .then(() => {
          this.updateMobileSignature({
            is_mobile_signature_approved: false,
            mobile_signature_expired_at: null,
          });
        })
        .catch(() => {})
        .finally(() => {
          this.mobile.logoutLoading = false;
        });
    },

    refreshUserMobileSignature() {
      // me endpoint'inden güncel mobile_signature verisini al
      this.send({ route: 'me' })
        .then((res) => {
          var data = res.data.data;
          // login response yapısı: data.mobile_signature veya data.user içinde
          var mobileSignature = data && (data.mobile_signature || null);
          if (mobileSignature) {
            this.updateMobileSignature({
              is_mobile_signature_approved: mobileSignature.is_mobile_signature_approved,
              mobile_signature_expired_at: mobileSignature.mobile_signature_expired_at,
            });
          }
          // auth_phone da güncellenebilir
          if (data && data.auth_phone !== undefined) {
            this.updateMobileSignature({
              is_mobile_signature_approved: mobileSignature ? mobileSignature.is_mobile_signature_approved : false,
              mobile_signature_expired_at: mobileSignature ? mobileSignature.mobile_signature_expired_at : null,
              auth_phone: data.auth_phone,
            });
          }
        })
        .catch(() => {});
    },

    // ---- e-İmza ----
    connectEimza() {
      if (this.eimza.ws) {
        this.eimza.ws.close();
      }
      this.eimza.checked = false;
      this.eimza.devices = [];
      this.eimza.authenticated = false;

      var ws = new WebSocket('ws://localhost:5134/ws');
      this.eimza.ws = ws;
      var self = this;

      ws.onopen = function () {
        self.pollDevices();
        self.startPolling();
      };

      ws.onmessage = function (event) {
        var msg = JSON.parse(event.data);
        if (msg.requestId && self.eimza.pendingResolvers[msg.requestId]) {
          self.eimza.pendingResolvers[msg.requestId](msg);
          delete self.eimza.pendingResolvers[msg.requestId];
        }
      };

      ws.onerror = function () {
        self.eimza.checked = true;
        self.stopPolling();
      };

      ws.onclose = function () {
        self.eimza.checked = true;
        self.stopPolling();
      };
    },

    wsSend(payload) {
      var self = this;
      var requestId = 'req_' + Math.random().toString(36).slice(2, 10);
      var message = Object.assign({ requestId: requestId }, payload);
      this.eimza.ws.send(JSON.stringify(message));
      return new Promise(function (resolve) {
        self.eimza.pendingResolvers[requestId] = resolve;
      });
    },

    pollDevices() {
      if (!this.eimza.ws || this.eimza.ws.readyState !== WebSocket.OPEN) return;
      var self = this;
      this.wsSend({ command: 'devices' }).then(function (msg) {
        self.handleDevicesResponse(msg);
      });
    },

    startPolling() {
      this.stopPolling();
      var self = this;
      this.eimza.pollTimer = setInterval(function () {
        self.pollDevices();
      }, 3000);
    },

    stopPolling() {
      if (this.eimza.pollTimer) {
        clearInterval(this.eimza.pollTimer);
        this.eimza.pollTimer = null;
      }
    },

    handleDevicesResponse(msg) {
      var devices = (msg.data && msg.data.data) || [];
      var hadDevices = this.eimza.devices.length > 0;
      var isFirstCheck = !this.eimza.checked;

      devices.forEach(function (device) {
        (device.certs || []).forEach(function (cert) {
          if (cert.subject) {
            var decoded = cert.subject.replace(/\\([0-9A-Fa-f]{2})/g, '%$1');
            var readable = decodeURIComponent(decoded);
            if (!cert.subjectName) {
              var cnMatch = readable.match(/CN=([^,]+)/);
              cert.subjectName = cnMatch ? cnMatch[1] : '';
            }
            if (!cert.subjectSerial) {
              var snMatch = readable.match(/serialNumber=([^,]+)/);
              cert.subjectSerial = snMatch ? snMatch[1] : '';
            }
          }
        });
      });

      this.eimza.devices = devices;
      this.eimza.checked = true;

      if (isFirstCheck) {
        if (devices.length > 0) {
          // localStorage yerine store'dan kontrol et
          if (this.eimzaStoreApproved) {
            this.eimza.authenticated = true;
          } else {
            if (this.autoOpenPin) {
              this.eimza.pinDialog = true;
            }
          }
        } else {
          this.sendEimzaLogout();
        }
        return;
      }

      if (!hadDevices && devices.length > 0 && !this.eimza.authenticated) {
        this.eimza.logoutSent = false;
        this.eimza.loginError = '';
        this.eimza.pin = '';
        if (this.autoOpenPin) {
          this.eimza.pinDialog = true;
        }
      }

      if (hadDevices && devices.length === 0) {
        this.eimza.authenticated = false;
        this.eimza.pinDialog = false;
        this.sendEimzaLogout();
      }
    },

    sendEimzaLogout() {
      if (this.eimza.logoutSent) return;
      this.eimza.logoutSent = true;
      this.send({ route: 'esignature/logout' });
    },

    doSignLogin() {
      if (!this.eimza.pin) return;
      var self = this;
      this.eimza.loginLoading = true;
      this.eimza.loginError = '';

      this.send({ route: 'esignature/login' })
        .then(function (res) {
          var loginTxId = res.data.data.loginTxId;
          var challenge = res.data.data.challenge;
          return self.wsSend({
            command: 'signLogin',
            pin: self.eimza.pin,
            slot: 0,
            istest: false,
            issecure: false,
            loginTxId: loginTxId,
            challenge: challenge,
          });
        })
        .then(function (result) {
          if (result.data && result.data.success) {
            return self.send({ route: 'esignature/set' }).then(function (setRes) {
              var expiresAt = setRes && setRes.data && setRes.data.data && setRes.data.data.esignature_expires_at;
              self.updateEsignature({
                esignature_is_valid: 1,
                esignature_expires_at: expiresAt || null,
              });
              self.eimza.authenticated = true;
              self.eimza.pinDialog = false;
              self.eimza.pin = '';
            });
          }
          var errMsg = (result.data && (result.data.error || result.data.message)) || 'Doğrulama başarısız';
          self.eimza.loginError = self.translateSignError(errMsg);
          return null;
        })
        .catch(function (err) {
          self.eimza.loginError = (typeof err === 'string' && err) ? err : 'Bir hata oluştu, lütfen tekrar deneyin.';
        })
        .finally(function () {
          self.eimza.loginLoading = false;
        });
    },

    translateSignError(error) {
      var map = {
        'Invalid PIN': 'Girdiğiniz PIN kodu hatalı. Lütfen tekrar deneyiniz.',
        'loginTx not found': 'Doğrulama oturumu bulunamadı. Lütfen sayfayı yenileyerek tekrar deneyiniz.',
        'Certificate not found': 'e-İmza sertifikası bulunamadı. Cihazınızın doğru takılı olduğundan emin olunuz.',
        'Invalid slot number': 'e-İmza cihazı okunamadı. Cihazı çıkarıp tekrar takınız.',
        'Certificate expired': 'e-İmza sertifikanızın süresi dolmuş. Lütfen sertifikanızı yenileyiniz.',
        'TCKN does not match': 'TC Kimlik Numarası e-İmza sertifikasıyla eşleşmiyor. Lütfen giriş yaptığınız doğrulama numaranıza kayıtlı TC Kimlik Numarasını veya ana kullanıcının T.C Kimlik Numarasını kontrol ediniz.',
      };
      return map[error] || error;
    },
  },
};
</script>
